<template>
  <div class="container-fluid py-4">

    <!-- FORM -->
    <div class="card mb-4 shadow-sm border-0">
      <div class="card-header" style="background:#b03a45;">
        <h6 class="mb-0 text-white">
          {{ form.id ? t("leaveRequests.edit") : t("leaveRequests.request") }}
        </h6>
      </div>

      <div class="card-body">
        <form @submit.prevent="submitLeave">
          <div class="row">

            <!-- TYPE -->
            <div class="col-md-4 mb-3">
              <label>{{ t("leaveRequests.type") }}</label>
              <select v-model="form.type" class="form-select" required>
                <option disabled value="">
                  {{ t("leaveRequests.selectType") }}
                </option>
                <option value="Sick">{{ t("leaveRequests.sick") }}</option>
                <option value="Maternity">{{ t("leaveRequests.maternity") }}</option>
                <option value="Paternity">{{ t("leaveRequests.paternity") }}</option>
                <option value="Other">{{ t("leaveRequests.other") }}</option>
              </select>
            </div>

            <!-- HALF DAY -->
            <div class="col-md-4 mb-3">
              <label>{{ t("leaveRequests.halfDay") }}</label>
              <select v-model="form.half_day" class="form-select">
                <option value="">{{ t("leaveRequests.fullDay") }}</option>
                <option value="am">AM</option>
                <option value="pm">PM</option>
              </select>
            </div>

            <!-- START DATE -->
            <div class="col-md-6 mb-3">
              <label>{{ t("leaveRequests.startDate") }}</label>
              <input
                type="date"
                v-model="form.start_date"
                class="form-control"
                required
              />
            </div>

            <!-- END DATE -->
            <div class="col-md-6 mb-3">
              <label>{{ t("leaveRequests.endDate") }}</label>
              <input
                type="date"
                v-model="form.end_date"
                class="form-control"
                required
              />
            </div>

            <!-- REASON -->
            <div class="col-12 mb-3">
              <label>{{ t("leaveRequests.reason") }}</label>
              <div class="border rounded p-2">
                <textarea
                  v-model="form.reason"
                  class="form-control border-0"
                  rows="4"
                  :placeholder="t('leaveRequests.reasonPlaceholder')"
                  style="resize: vertical;"
                ></textarea>
              </div>
            </div>

            <!-- CERTIFICATE -->
            <div class="col-md-6 mb-3">
              <label>{{ t("leaveRequests.medicalCertificate") }}</label>
              <input
                type="file"
                class="form-control"
                accept=".pdf,.jpg,.jpeg,.png"
                @change="handleFileChange"
              />
            </div>

            <div class="col-md-6 d-flex align-items-end">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="form.is_certified"
                  id="certified"
                />
                <label class="form-check-label" for="certified">
                  {{ t("leaveRequests.certifiedLeave") }}
                </label>
              </div>
            </div>

            <!-- BUTTON -->
            <div class="col-12 text-end">
              <button class="btn text-white" style="background:#b03a45;">
                {{ form.id ? t("leaveRequests.save") : t("leaveRequests.submit") }}
              </button>
            </div>

          </div>
        </form>
      </div>
    </div>

    <!-- BALANCE -->
    <div class="card mb-4" v-if="myBalance">
      <div class="card-header text-white" style="background:#2c7a7b;">
        <h6 class="mb-0">{{ t("leaveRequests.myBalance") }}</h6>
      </div>

      <div class="card-body">
        <div class="row">

          <div class="col-md-6">
            <h6 class="mb-2">{{ t("leaveRequests.annualLeave") }}</h6>
            <div>{{ t("leaveRequests.entitlement") }}: <b>{{ myBalance.annual_entitlement }}</b></div>
            <div>{{ t("leaveRequests.accrued") }}: <b>{{ myBalance.annual_accrued }}</b></div>
            <div>{{ t("leaveRequests.used") }}: <b>{{ myBalance.annual_used }}</b></div>
            <div>{{ t("leaveRequests.remaining") }}: <b>{{ myBalance.annual_remaining }}</b></div>
          </div>

          <div class="col-md-6">
            <h6 class="mb-2">{{ t("leaveRequests.sickLeave") }}</h6>
            <div>{{ t("leaveRequests.entitlement") }}: <b>{{ myBalance.sick_entitlement }}</b></div>
            <div>{{ t("leaveRequests.accrued") }}: <b>{{ myBalance.sick_accrued }}</b></div>
            <div>{{ t("leaveRequests.used") }}: <b>{{ myBalance.sick_used }}</b></div>
            <div>{{ t("leaveRequests.remaining") }}: <b>{{ myBalance.sick_remaining }}</b></div>
          </div>

        </div>
      </div>
    </div>

    <div v-else class="alert alert-warning">
      {{ t("leaveRequests.noBalance") }}
    </div>

    <!-- LEAVE LIST -->
    <h5>{{ t("leaveRequests.myRequests") }}</h5>

    <table class="table">
      <thead>
        <tr>
          <th>{{ t("leaveRequests.type") }}</th>
          <th>{{ t("leaveRequests.dates") }}</th>
          <th>{{ t("leaveRequests.days") }}</th>
          <th>{{ t("leaveRequests.status") }}</th>
          <th>{{ t("leaveRequests.reason") }}</th>
          <th>{{ t("leaveRequests.certificate") }}</th>
          <th>{{ t("leaveRequests.editBtn") }}</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="leave in myLeaves" :key="leave.id">
          <td>{{ $t(`leaveRequests.${leave.type.toLowerCase()}`) || leave.type }}</td>
          <td>{{ formatDate(leave.start_date) }} → {{ formatDate(leave.end_date) }}</td>
          <td>{{ leave.days }}</td>
          <td>
            <span :class="{
              'text-warning': leave.status === 'Pending',
              'text-success': leave.status === 'Approved',
              'text-danger': leave.status === 'Rejected'
            }">
              {{
                leave.status === "Pending"
                  ? t("leaveRequests.pending")
                  : leave.status === "Approved"
                  ? t("leaveRequests.approved")
                  : t("leaveRequests.rejected")
              }}
            </span>
          </td>

          <td>{{ leave.reason || "—" }}</td>

          <td>
            <a
              v-if="leave.certificate_path"
              :href="getUrl(leave.certificate_path)"
              target="_blank"
              class="btn btn-sm btn-outline-danger"
            >
              {{ t("leaveRequests.view") }}
            </a>
            <span v-else>—</span>
          </td>

          <td>
            <button
              v-if="leave.status === 'Pending'"
              class="btn btn-sm"
              style="background:#b03a45;color:white;"
              @click="startEdit(leave)"
            >
              ✏️
            </button>
          </td>
        </tr>
      </tbody>
    </table>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import axios from "@/api";
import { useToast } from "vue-toastification";

const { t } = useI18n();
const toast = useToast();

// ---------- FORM ----------
const form = ref({
  id: null,
  type: "",
  start_date: "",
  end_date: "",
  half_day: "",
  reason: "",
  is_certified: false,
  certificate: null,
});

// ---------- DATA ----------
const leaves = ref([]);
const balance = ref(null);

const fetchLeaves = async () => {
  const { data } = await axios.get("/employee/leaves/me");
  leaves.value = data.leaves || [];
  balance.value = data.my_balance || null;
};

const myLeaves = computed(() => leaves.value);
const myBalance = computed(() => balance.value);

// ---------- FILE ----------
const handleFileChange = (e) => {
  form.value.certificate = e.target.files[0];
  form.value.is_certified = true;
};

// ---------- UTILS ----------
const formatDate = (date) =>
  date ? String(date).split("T")[0] : "";

// ---------- EDIT ----------
const startEdit = (leave) => {
  form.value = {
    id: leave.id,
    type: leave.type,
    start_date: formatDate(leave.start_date),
    end_date: formatDate(leave.end_date),
    half_day: leave.half_day ?? "",
    reason: leave.reason ?? "",
    is_certified: leave.is_certified ?? false,
    certificate: null,
  };
  window.scrollTo({ top: 0, behavior: "smooth" });
};

// ---------- SUBMIT ----------
const submitLeave = async () => {
  const payload = new FormData();

  payload.append("type", form.value.type);
  payload.append("start_date", form.value.start_date);
  payload.append("end_date", form.value.end_date);
  payload.append("is_certified", form.value.is_certified ? 1 : 0);

  if (form.value.half_day) payload.append("half_day", form.value.half_day);
  if (form.value.reason) payload.append("reason", form.value.reason);
  if (form.value.certificate) payload.append("certificate", form.value.certificate);

  try {
    if (form.value.id) {
      payload.append("_method", "PUT");
      await axios.post(`/employee/leaves/${form.value.id}`, payload);
      toast.success(t("toast.updated"));
    } else {
      await axios.post("/employee/leaves", payload);
      toast.success(t("toast.created"));
    }

    await fetchLeaves();
    resetForm();
  } catch (err) {
    toast.error(t("toast.error"));
    console.error(err);
  }
};

// ---------- RESET ----------
const resetForm = () => {
  form.value = {
    id: null,
    type: "",
    start_date: "",
    end_date: "",
    half_day: "",
    reason: "",
    is_certified: false,
    certificate: null,
  };
};

// ---------- FILE URL ----------
const API_URL = process.env.VUE_APP_API_URL;
const getUrl = (path) =>
  `${API_URL.replace(/\/$/, "")}/storage/${path}`;

onMounted(fetchLeaves);
</script>
